<template>
  <div
    v-if="text"
    class="business-intelligence-badge"
    :class="`__variant-${variant} __size-${size}`"
    @mouseenter="hover = true"
    @mouseleave="hover = false"
  >
    <div class="__single-text">
      <div v-if="$slots.icon" class="__icon-container">
        <slot name="icon" />
      </div>
      <span ref="badgeContainer" :key="text" class="__title" v-tooltip="tooltipOptions(text)">{{ text }}</span>
      <div class="__action-container">
        <BaseIcon
          v-if="tenantHasBusinessIntelligenceAccess() && data && !data.is_manual_entry"
          name="verified-badge"
          v-tooltip="source ? $t('page.business-intelligence.official-results', { source }) : ''"
        />
        <BIBadgeActionMenu
          v-if="tenantHasBusinessIntelligenceAccess() && data"
          class="action-button"
          :type="type"
          :name="text"
          :hover="hover"
          :isExistingCompetitor="data.is_existing_competitor"
          :isTenant="data.is_tenant"
          :isCompetitor="data.is_competitor"
          :isManualEntry="data.is_manual_entry"
          :hasWinner="hasWinner"
          @add-competitor="onAddCompetitorClicked"
          @edit-competitor="onEditCompetitorClicked"
          @add-winner="onAddWinnerClicked"
          @remove-winner="onRemoveWinnerClicked"
        />
      </div>
    </div>
  </div>
  <div
    v-else
    class="business-intelligence-badge"
    :class="`__variant-${variant} __size-${size}`"
    @mouseenter="hover = true"
    @mouseleave="hover = false"
  >
    <div v-if="$slots.icon" class="__icon-container">
      <slot name="icon" />
    </div>
    <div class="__content-container">
      <div class="__row">
        <span class="__index" v-if="hasValidAmount && !$slots.icon">#{{ displayIndex }}</span>
        <span
          ref="badgeContainer"
          :key="data.supplier_name"
          class="__title"
          v-tooltip="tooltipOptions(data.supplier_name)"
        >
          {{ data.supplier_name }}
        </span>
        <BaseIcon
          name="verified-badge"
          v-if="tenantHasBusinessIntelligenceAccess() && !data.is_manual_entry"
          v-tooltip="$t('page.business-intelligence.official-results', { source })"
        />
        <BIBadgeActionMenu
          class="action-button"
          v-if="tenantHasBusinessIntelligenceAccess() && variant !== 'muted'"
          :type="type"
          :name="data.supplier_name"
          :hover="hover"
          :hasWinner="hasWinner"
          :isExistingCompetitor="data.is_existing_competitor"
          :isTenant="data.is_tenant"
          :isManualEntry="data.is_manual_entry"
          :isCompetitor="data.is_competitor"
          @add-competitor="onAddCompetitorClicked"
          @edit-competitor="onEditCompetitorClicked"
          @add-winner="onAddWinnerClicked"
          @remove-winner="onRemoveWinnerClicked"
        />
      </div>
      <div class="__row">
        <div class="__amount-container">
          <div class="__amount number-monospace" :class="{ 'invalid-amount': !hasValidAmount }">
            {{ displayCurrency(data.amount, 0) }}
          </div>
          <slot name="chip" v-if="hasValidAmount" />
        </div>
        <div>
          <BIBadgeCompetitorMenu
            class="action-button"
            v-if="
              tenantHasBusinessIntelligenceAccess() && !(data.is_tenant || data.is_competitor) && variant !== 'muted'
            "
            :name="data.supplier_name"
            :hover="hover"
            :isExistingCompetitor="data.is_existing_competitor"
            @action-succeeded="onActionSucceeded"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import BIBadgeActionMenu from '@/components/page-business-intelligence/BIBadgeActionMenu.vue';
import BIBadgeCompetitorMenu from '@/components/page-business-intelligence/BIBadgeCompetitorMenu.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { displayCurrency } from '@/util/currency-util';
import { tenantHasBusinessIntelligenceAccess } from '@/util/module-access-util';

export default {
  name: 'BIBadge',
  components: { BIBadgeActionMenu, BIBadgeCompetitorMenu, BaseIcon },
  props: {
    type: {
      type: String, // bidder | winner
      required: true,
    },
    variant: {
      type: String,
      required: false,
      default: 'default', // default | highlight | active | emphasis | muted
    },
    size: {
      type: String,
      required: false,
      default: 'compact', // compact | full
    },
    displayIndex: {
      type: Number,
      required: false,
      default: null,
    },
    data: {
      type: Object,
      required: false,
    },
    text: {
      type: String,
      required: false,
    },
    hasWinner: {
      type: Boolean,
      required: false,
      default: false,
    },
    source: {
      type: String,
      required: false,
      default: null,
    },
  },
  data: () => ({ badgeContainer: null, hover: false }),
  computed: {
    hasValidAmount() {
      return this.data.amount !== null && this.data.amount !== undefined && !isNaN(this.data.amount);
    },
  },
  methods: {
    tenantHasBusinessIntelligenceAccess,
    displayCurrency,
    tooltipOptions(text) {
      if (this.badgeContainer && this.isTruncated(text)) {
        return {
          content: text,
        };
      }
      return {};
    },
    isTruncated(text) {
      if (text && this.badgeContainer) {
        // Compare scrollWidth (full content width) with clientWidth (visible width)
        return this.badgeContainer.scrollWidth > this.badgeContainer.clientWidth;
      } else {
        return false;
      }
    },
    onActionSucceeded() {
      this.$emit('action-succeeded');
    },
    onAddCompetitorClicked(name) {
      this.$emit('add-competitor', name);
    },
    onEditCompetitorClicked(competitor, name) {
      this.$emit('edit-competitor', competitor, name);
    },
    onAddWinnerClicked() {
      this.$emit('add-winner', this.data);
    },
    onRemoveWinnerClicked() {
      this.$emit('remove-winner', this.data.supplier_id);
    },
  },
  mounted() {
    if (this.$refs.badgeContainer) {
      this.badgeContainer = this.$refs.badgeContainer;
    }
  },
  updated() {
    if (this.$refs.badgeContainer) {
      this.badgeContainer = this.$refs.badgeContainer;
    }
  },
};
</script>

<style lang="scss">
.business-intelligence-badge {
  display: flex;
  min-height: 60px;
  border-radius: $border-radius-medium;
  color: $color-normal;
  align-items: center;
  padding: 0px 6px;

  &:hover:not(.__variant-muted, .__variant-active) {
    background-color: $color-neutral-pale;
  }

  .__icon-container {
    border-radius: $border-radius-medium;
    padding: 8px;

    .base-icon {
      color: $color-success;
    }
  }

  &.__size-compact {
    width: 185px;

    .__content-container {
      display: grid;
      grid-template-rows: 1fr auto;
      width: 100%;
    }
  }

  &.__size-full {
    width: 100%;

    .__content-container {
      display: grid;
      grid-template-rows: 1fr auto;
      width: 100%;
    }
  }

  .base-icon {
    width: 18px;
    height: 18px;
  }

  &.__variant-highlight {
    border: 1px solid $color-accent;
    color: $color-normal;

    .__icon-container {
      background-color: $color-success-pale;

      .base-icon {
        color: $color-success;
      }
    }
  }
  &.__variant-emphasis {
    border: 1px solid $color-accent;

    .__icon-container {
      background-color: $color-success-pale;

      .base-icon {
        color: $color-success;
      }
    }
  }
  &.__variant-active {
    background-color: $color-accent-pale;
    border: 1px solid $color-accent;

    .__icon-container {
      background-color: $color-success-pale;

      .base-icon {
        color: $color-success;
      }
    }
  }
  &.__variant-default {
    border: 1px solid $color-neutral;

    .__icon-container {
      background-color: $color-success-pale;

      .base-icon {
        color: $color-success;
      }
    }
  }

  &.__variant-muted {
    border: 1px dashed rgba($color-normal-pale, 0.5);
    color: rgba($color-normal-pale, 0.5);
    font-style: italic;

    .__icon-container {
      background-color: rgba($color-neutral-pale, 0.5);

      .base-icon {
        color: rgba($color-normal-pale, 0.5);
      }
    }
  }

  .__single-text {
    display: flex;
    justify-content: space-between;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    gap: 8px;
    width: 100%;
    font-size: $font-size-xsmall;

    .__title {
      width: 100%;
      align-items: center;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .base-icon {
      width: 20px;
      height: 20px;
    }

    .__action-container {
      display: flex;
      align-items: center;
    }
  }

  .__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    overflow: hidden;

    &:first-child {
      padding: 8px 8px 0px 8px;
    }

    &:last-child {
      min-height: 30px;
      padding: 4px 8px 8px 8px;
    }

    .base-icon {
      flex: 0 0 auto;
    }

    .__title {
      flex: 1 1 auto; /* the only item that grows/shrinks */
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: 700;
      font-size: $font-size-xsmall;
    }

    .__index {
      flex: 0 0 auto;
      white-space: nowrap;
      font-weight: 700;
      font-size: $font-size-xsmall;
      margin-right: 4px;
    }

    .__amount-container {
      display: flex;
      align-items: center;
      gap: 4px;

      .__amount {
        flex: 1 auto;
        font-size: $font-size-xsmall;
        color: $color-normal-pale;

        &.invalid-amount {
          color: $color-normal-pale;
        }
      }

      .base-icon {
        width: 10px;
        height: 10px;
        color: $color-normal;
      }
    }
  }
}
</style>
